import styles from "./AssistantUpdateModal.module.css";
import { Modal } from "react-bootstrap";
import { useMemo, useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { selectTeams } from "../redux/slices/teamsSlice";
import { selectUser } from "../redux/slices/userSlice";
import { selectAssistants, setUpdateForm, updateAssistant } from "../redux/slices/assistantsSlice.js";
import { useToaster } from "./Toaster";
import Select from "react-select";
import FileModal2 from "./FileModal2";

export default function AssistantUpdateModal({ modalOpen, onHide }) {
  const teams = useSelector(selectTeams);
  const user = useSelector(selectUser);
  const assistants = useSelector(selectAssistants);
  const dispatch = useDispatch();
  const { push } = useToaster();
  const [fileModalOpen, setFileModalOpen] = useState(false);

  const rootEl = typeof document !== "undefined" ? document.getElementById("root") : null;

  const isDisabled = useMemo(() => {
    const selected = teams.teams?.find((item) => item.token === teams.team_selected);
    const role = selected?.account_role;
    return Boolean((teams.teams?.length ?? 0) > 0 && ![1, 2].includes(role));
  }, [teams.teams, teams.team_selected]);

  const assistantDefaultOption = useMemo(() => {
    return (assistants?.assistant_default ?? []).map((item) => ({
      value: item.id,
      label: item.option_name,
    }));
  }, [assistants?.assistant_default]);
  
  useEffect(() => {
    const allowed = new Set((assistants?.fileOptions ?? []).map((x) => x.file_token));
    const current = assistants?.createForm?.files ?? [];
    const filtered = current.filter((t) => allowed.has(t));
    const same =
      current.length === filtered.length &&
      current.every((v, i) => v === filtered[i]);

    if (same) return;
    if (!assistants?.updateForm) return;

    dispatch(
      setUpdateForm({
        ...assistants.updateForm,
        files: filtered,
      })
    );
  }, [assistants?.fileOptions, dispatch]);

  const selectedFilesCount = (assistants?.updateForm?.files ?? []).length;

  const handleInputChange = (e) => {
    dispatch(
      setUpdateForm({
        ...assistants.updateForm,
        [e.target.name]: e.target.value,
      })
    );
  };

  const onAssistantDefaultChange = (opt) => {
    dispatch(
      setUpdateForm({
        ...assistants.updateForm,
        assistant_default: opt?.value || "",
      })
    );
  };

  const handleSubmit = () => {
    const form = assistants?.updateForm || {};

    if (!form?.name) {
      push({ title: "表單有誤", message: "請輸入代理人名稱！", variant: "danger" });
      return;
    }

    if (!form?.assistant_default) {
      push({ title: "表單有誤", message: "請選擇代理人類型！", variant: "danger" });
      return;
    }

    if (!form?.instructions) {
      push({ title: "表單有誤", message: "請輸入系統指令（Prompt）！", variant: "danger" });
      return;
    }

    dispatch(updateAssistant(form));
  };

  const selectStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 2400 }),
    control: (base, state) => ({
      ...base,
      backgroundColor: state.isDisabled ? "#eef0f5" : "#ffffff",
      borderColor: state.isFocused ? "#d84040" : "#d0d3d8",
      boxShadow: state.isFocused
        ? "inset 0 1px 2px rgba(0,0,0,.10), 0 0 0 2px #fff, 0 0 0 4px rgba(255,64,64,.5)"
        : "inset 0 1px 2px rgba(0,0,0,.08)",
      borderRadius: 10,
      minHeight: 40,
      outline: "none",
      transition: "box-shadow .2s ease, border-color .2s ease",
      cursor: state.isDisabled ? "not-allowed" : "default",
      opacity: state.isDisabled ? 0.85 : 1,
      ":hover": {
        borderColor: state.isFocused ? "#d84040" : "rgba(0,0,0,.2)",
      },
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? "rgba(16,16,20,.55)" : "#101014",
      fontWeight: 700,
    }),
    placeholder: (base) => ({ ...base, color: "#868e96", fontWeight: 600 }),
    dropdownIndicator: (base, state) => ({
      ...base,
      color: state.isFocused ? "#d84040" : "#495057",
      ":hover": { color: "#d84040" },
    }),
    indicatorSeparator: (base) => ({ ...base, backgroundColor: "rgba(0,0,0,.12)" }),
    menu: (base) => ({
      ...base,
      overflow: "hidden",
      marginTop: 6,
      borderRadius: 10,
      backgroundColor: "#ffffff",
      border: "1px solid #d84040",
      boxShadow: "0 18px 36px rgba(0,0,0,.35), 0 0 0 1px rgba(0,0,0,.1)",
      zIndex: 2400,
    }),
    menuList: (base) => ({
      ...base,
      padding: 6,
      backgroundColor: "#ffffff",
    }),
    option: (base, state) => ({
      ...base,
      padding: "10px 14px",
      borderRadius: 8,
      transition: "background .15s ease, color .15s ease, box-shadow .15s ease",
      fontWeight: state.isSelected ? 800 : 650,
      color: state.isDisabled ? "#ccc" : "#101014",
      backgroundColor: state.isSelected || state.isFocused ? "rgba(216,64,64,.1)" : "transparent",
      boxShadow: state.isSelected || state.isFocused ? "inset 4px 0 0 0 #d84040" : "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      ":active": {
        backgroundColor: state.isDisabled ? "transparent" : "rgba(216,64,64,.14)",
      },
    }),
  };

  const form = assistants?.updateForm || {};
  const nameVal = form?.name ?? "";
  const descVal = form?.description ?? "";
  const instVal = form?.instructions ?? "";
  const defaultVal = form?.assistant_default ?? "";

  return (
    <>
      <Modal
        show={modalOpen}
        onHide={onHide}
        backdrop="static"
        scrollable
        fullscreen
        className={styles.modalRoot}
        dialogClassName={styles.dialog}
        contentClassName={styles.modalContent}
        backdropClassName={styles.backdrop}
        container={rootEl}
      >
        <Modal.Header className={styles.modalHeader} closeButton closeVariant="white" closeLabel="關閉">
          <Modal.Title className={styles.modalTitle}>
            <i className="bi bi-pencil-square" aria-hidden="true" />
            編輯代理人
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className={styles.modalBody}>
          <div className={styles.bodyInner}>
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <div className={styles.panelTitle}>
                  <span className={styles.labelDot} aria-hidden="true" />
                  代理人設定
                </div>
                <div className={styles.panelHint}>
                  變更後點擊「送出更新」，會立即套用到此代理人。
                </div>
              </div>

              <form
                className={styles.form}
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSubmit();
                }}
              >
                <div className={styles.grid2}>
                  <div className={styles.field}>
                    <label htmlFor="assistant-update_name" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDotSmall} aria-hidden="true" />
                      代理人名稱 <span className="text-danger">*</span>
                    </label>

                    <input
                      type="text"
                      className={`form-control ${styles.input}`}
                      id="assistant-update_name"
                      placeholder="輸入代理人名稱"
                      name="name"
                      value={nameVal}
                      onChange={handleInputChange}
                      maxLength={20}
                      disabled={isDisabled}
                    />

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 20 字。</small>
                      <small className={styles.charCount}>{nameVal.length}/20</small>
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label htmlFor="assistant-update_assistant_default" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDotSmall} aria-hidden="true" />
                      代理人類型 <span className="text-danger">*</span>
                    </label>

                    <Select
                      inputId="assistant-update_assistant_default"
                      instanceId="assistant-update_assistant_default"
                      className={styles.select}
                      options={assistantDefaultOption}
                      value={assistantDefaultOption.find((o) => o.value === defaultVal) || null}
                      onChange={onAssistantDefaultChange}
                      placeholder="選擇代理人類型"
                      isSearchable
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      isDisabled={isDisabled}
                      styles={selectStyles}
                    />
                  </div>
                </div>

                <div className={styles.grid1}>
                  <div className={styles.field}>
                    <label htmlFor="assistant-update_description" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDotSmall} aria-hidden="true" />
                      代理人說明
                    </label>

                    <input
                      type="text"
                      className={`form-control ${styles.input}`}
                      id="assistant-update_description"
                      placeholder="輸入代理人說明"
                      name="description"
                      value={descVal}
                      onChange={handleInputChange}
                      maxLength={50}
                      disabled={isDisabled}
                    />

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 50 字。例如：用於回答客戶在官網上遇到的問題</small>
                      <small className={styles.charCount}>{descVal.length}/50</small>
                    </div>
                  </div>
                </div>

                <div className={styles.grid1}>
                  <div className={styles.field}>
                    <label htmlFor="assistant-update_instructions" className={`${styles.fieldLabel} form-label`}>
                      <span className={styles.labelDotSmall} aria-hidden="true" />
                      系統指令（Prompt） <span className="text-danger">*</span>
                    </label>

                    <textarea
                      className={`form-control ${styles.input} ${styles.textarea}`}
                      id="assistant-update_instructions"
                      name="instructions"
                      placeholder="請輸入 AI 的系統指令，例如：你是一位擅長客服支援的專業助理，請用親切口吻回答使用者問題..."
                      rows={8}
                      maxLength={1000}
                      value={instVal}
                      onChange={handleInputChange}
                      disabled={isDisabled}
                    />

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>最多 1000 字。建議清楚描述 AI 的角色、風格與限制。</small>
                      <small className={styles.charCount}>{instVal.length}/1000</small>
                    </div>
                  </div>
                </div>

                <div className={styles.grid1}>
                  <div className={styles.field}>
                    <div className={styles.inlineHeader}>
                      <label htmlFor="assistant-update_files" className={`${styles.fieldLabel} form-label mb-0`}>
                        <span className={styles.labelDotSmall} aria-hidden="true" />
                        背景知識
                      </label>
                      <div className={styles.inlineMeta}>
                        已選 <span className={styles.inlineMetaStrong}>{selectedFilesCount}</span> 個
                      </div>
                    </div>

                    <button
                      type="button"
                      id="assistant-update_files"
                      className={styles.btnUpload}
                      onClick={() => setFileModalOpen(true)}
                      disabled={isDisabled}
                    >
                      <i className="bi bi-list-check" aria-hidden="true" />
                      選擇檔案
                    </button>

                    <div className={styles.helpRow}>
                      <small className={styles.helpText}>
                        你可以在這裡挑選要當作代理人背景知識的檔案（PDF / TXT）。
                      </small>
                      <small className={styles.charCount}>{selectedFilesCount}</small>
                    </div>
                  </div>
                </div>

                <div className={styles.actions}>
                  <div className={styles.actionsRight}>
                    <button type="button" className={styles.btnGhost} onClick={onHide}>
                      <i className="bi bi-x-lg" aria-hidden="true" />
                      取消
                    </button>
                    <button
                      type="button"
                      className={styles.btnSave}
                      onClick={handleSubmit}
                      disabled={isDisabled}
                    >
                      <i className="bi bi-send-check" aria-hidden="true" />
                      送出更新
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <FileModal2 modalOpen={fileModalOpen} onHide={() => setFileModalOpen(false)} />
    </>
  );
}
